import { useEffect, useRef, useState } from "react";
import { motion, AnimatePresence } from "framer-motion";
import { Calendar, X, PartyPopper, Loader2, Play, Volume2, VolumeX, Gift, Sparkles } from "lucide-react";
import { toast } from "sonner";
import { MaskLinesInView, Reveal } from "../components/Reveal";
import { BrandMarquee } from "../components/BrandMarquee";
import { getEvents, hostJam } from "../lib/api";

const IG_URL = "https://www.instagram.com/myfeatherbomb/";
const IG_DM = "https://ig.me/m/myfeatherbomb";

const withIG = (text) =>
  text.split("@myfeatherbomb").flatMap((part, i) =>
    i === 0
      ? [part]
      : [
          <a
            key={i}
            href={IG_URL}
            target="_blank"
            rel="noreferrer"
            className="font-semibold text-[#EE4026] underline decoration-[#EE4026]/40 underline-offset-2 hover:decoration-[#EE4026]"
          >
            @myfeatherbomb
          </a>,
          part,
        ]
  );

const VideoTile = ({ src }) => {
  const ref = useRef(null);
  const [muted, setMuted] = useState(true);
  const toggle = () => {
    const v = ref.current;
    if (!v) return;
    v.muted = !v.muted;
    setMuted(v.muted);
    if (!v.muted) v.play().catch(() => {});
  };
  return (
    <div className="relative h-full">
      <video
        ref={ref}
        src={src}
        autoPlay
        muted
        loop
        playsInline
        onClick={toggle}
        className="aspect-[3/4] w-full cursor-pointer object-cover"
      />
      {muted && (
        <button
          onClick={toggle}
          data-testid="flock-video-sound-hint"
          className="absolute bottom-3 left-3 flex items-center gap-1.5 rounded-full bg-[#212121]/70 px-3 py-1.5 font-mono text-[10px] uppercase tracking-widest text-white backdrop-blur transition hover:bg-[#EE4026]"
        >
          <VolumeX size={13} /> Tap for sound
        </button>
      )}
      <button
        onClick={toggle}
        data-testid="flock-video-sound-toggle"
        aria-label={muted ? "Unmute video" : "Mute video"}
        className="absolute bottom-3 right-3 grid h-10 w-10 place-items-center rounded-full bg-[#212121]/70 text-white backdrop-blur transition hover:bg-[#EE4026]"
      >
        {muted ? <VolumeX size={16} /> : <Volume2 size={16} />}
      </button>
    </div>
  );
};

const FILTERS = ["All", "Solo", "Duo", "Flock"];

const RECORDS = [
  { cat: "Solo", title: "Personal Best", desc: "Most consecutive kicks by one player.", record: "25 Kicks", who: "@iskanghui", cta: "Submit Your Score" },
  { cat: "Duo", title: "Best Rally", desc: "Two people. One Featherbomb. How long can you keep it going?", record: "33 Kicks", who: "@klai + @betsjin", cta: "Submit Your Rally" },
  { cat: "Flock", title: "Best Rally", desc: "Three or more players. Keep it in the air for as long as you can.", record: "28 Kicks", who: "@stacksw + the flock", cta: "Submit Your Rally" },
];

const STEPS = [
  ["01", "Play", "Solo, duo or flock. Keep it up as long as you can."],
  ["02", "Film It", "Get your run on camera so we can count it."],
  ["03", "Tag Us", "Post it and tag @myfeatherbomb on Instagram."],
  ["04", "Get on the Board", "We'll update the Flock Board with the month's best runs."],
];

const fmtDate = (iso) =>
  new Date(iso + "T00:00:00").toLocaleDateString("en-US", { month: "short", day: "numeric", year: "numeric" });

const SubmissionCard = ({ ev, index }) => (
  <Reveal delay={index * 0.08}>
    <div
      data-testid={`community-event-card-${index + 1}`}
      className="group flex h-full flex-col overflow-hidden rounded-3xl border border-black/10 bg-white transition hover:-translate-y-1 hover:shadow-2xl"
    >
      <div className="relative aspect-[16/10] overflow-hidden">
        <img src={ev.image} alt={ev.name} className="h-full w-full object-cover transition duration-700 group-hover:scale-105" />
        <span className="absolute left-4 top-4 rounded-full bg-[#EE4026] px-3 py-1 font-mono text-[10px] uppercase tracking-widest text-white">
          {ev.category}
        </span>
        <div className="absolute bottom-3 right-3 rounded-2xl bg-[#212121]/85 px-4 py-2 text-center text-white backdrop-blur">
          <p className="font-display text-2xl leading-none">{ev.kicks}</p>
          <p className="font-mono text-[9px] uppercase tracking-widest">kicks</p>
        </div>
      </div>
      <div className="flex flex-1 flex-col p-6">
        <h3 className="font-display text-2xl uppercase leading-none text-[#212121]">{ev.name}</h3>
        <p className="mt-2 font-mono text-xs uppercase tracking-wide text-[#184786]">
          {ev.category} · {ev.city}, {ev.country}
        </p>
        <div className="mt-5 flex flex-1 items-end justify-between">
          <span className="flex items-center gap-1.5 text-xs text-[#212121]/50">
            <Calendar size={13} /> {fmtDate(ev.date)}
          </span>
          <a
            href={ev.video}
            target="_blank"
            rel="noreferrer"
            data-testid="community-watch-run-button"
            className="btn-primary flex items-center gap-2 rounded-full px-5 py-2.5 text-xs font-bold uppercase tracking-wide"
          >
            <Play size={13} /> Watch Run
          </a>
        </div>
      </div>
    </div>
  </Reveal>
);

const HostModal = ({ open, onClose }) => {
  const [form, setForm] = useState({ name: "", email: "", city: "", message: "" });
  const [loading, setLoading] = useState(false);

  const submit = async (e) => {
    e.preventDefault();
    setLoading(true);
    try {
      await hostJam(form);
      toast.success("Request received!", { description: "We'll reach out to help you set up your local jam." });
      setForm({ name: "", email: "", city: "", message: "" });
      onClose();
    } catch {
      toast.error("Something went wrong", { description: "Please check your details and try again." });
    } finally {
      setLoading(false);
    }
  };

  return (
    <AnimatePresence>
      {open && (
        <motion.div
          className="fixed inset-0 z-[60] flex items-end justify-center sm:items-center"
          initial={{ opacity: 0 }} animate={{ opacity: 1 }} exit={{ opacity: 0 }}
          data-testid="host-event-modal"
        >
          <div className="absolute inset-0 bg-[#212121]/70 backdrop-blur-sm" onClick={onClose} />
          <motion.div
            initial={{ y: 60, opacity: 0 }} animate={{ y: 0, opacity: 1 }} exit={{ y: 40, opacity: 0 }}
            transition={{ duration: 0.4, ease: [0.22, 1, 0.36, 1] }}
            className="relative w-full max-w-md rounded-t-3xl bg-white p-7 shadow-2xl sm:rounded-3xl"
          >
            <button onClick={onClose} className="absolute right-5 top-5 grid h-9 w-9 place-items-center rounded-full bg-black/5 hover:bg-black/10">
              <X size={18} />
            </button>
            <p className="font-mono text-xs uppercase tracking-[0.25em] text-[#9E9E9E]">Start a circle</p>
            <h3 className="font-display mt-2 text-3xl uppercase text-[#212121]">Host a local jam</h3>
            <p className="mt-2 text-sm text-[#212121]/60">Tell us where you're playing and we'll help you rally a crew.</p>
            <form onSubmit={submit} className="mt-6 space-y-3">
              <input required placeholder="Your name" data-testid="host-form-name"
                value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })}
                className="w-full rounded-xl border border-black/15 px-4 py-3 text-sm outline-none focus:border-[#EE4026]" />
              <input required type="email" placeholder="Email" data-testid="host-form-email"
                value={form.email} onChange={(e) => setForm({ ...form, email: e.target.value })}
                className="w-full rounded-xl border border-black/15 px-4 py-3 text-sm outline-none focus:border-[#EE4026]" />
              <input required placeholder="City" data-testid="host-form-city"
                value={form.city} onChange={(e) => setForm({ ...form, city: e.target.value })}
                className="w-full rounded-xl border border-black/15 px-4 py-3 text-sm outline-none focus:border-[#EE4026]" />
              <textarea placeholder="Tell us about your spot (optional)" rows={3} data-testid="host-form-message"
                value={form.message} onChange={(e) => setForm({ ...form, message: e.target.value })}
                className="w-full resize-none rounded-xl border border-black/15 px-4 py-3 text-sm outline-none focus:border-[#EE4026]" />
              <button type="submit" disabled={loading} data-testid="host-form-submit"
                className="btn-primary flex w-full items-center justify-center gap-2 rounded-full py-4 text-sm font-bold uppercase tracking-wide disabled:opacity-70">
                {loading ? <Loader2 className="animate-spin" size={18} /> : <PartyPopper size={18} />}
                Send request
              </button>
            </form>
          </motion.div>
        </motion.div>
      )}
    </AnimatePresence>
  );
};

const Community = () => {
  const [events, setEvents] = useState([]);
  const [filter, setFilter] = useState("All");
  const [modal, setModal] = useState(false);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    getEvents()
      .then(setEvents)
      .catch(() => toast.error("Couldn't load the board"))
      .finally(() => setLoading(false));
  }, []);

  const shown = filter === "All" ? events : events.filter((e) => e.category === filter);

  return (
    <div data-testid="community-page">
      {/* Hero */}
      <section className="grain relative overflow-hidden bg-[#212121] px-5 pb-16 pt-32 sm:px-8">
        <div className="pointer-events-none absolute -left-20 top-10 h-80 w-80 rounded-full bg-[#EE4026]/30 blur-3xl" />
        <div className="pointer-events-none absolute -right-10 bottom-0 h-80 w-80 rounded-full bg-[#184786]/40 blur-3xl" />
        <div className="relative mx-auto max-w-[1400px]">
          <p className="font-mono text-xs uppercase tracking-[0.3em] text-[#EE4026]">The Community</p>
          <h1 className="font-display mt-5 text-white">
            <MaskLinesInView
              className="block text-[13vw] uppercase leading-[0.95] sm:text-8xl sm:leading-[0.85]"
              lines={["Birds of a Feather", <span key="c" className="text-[#EE4026]">Flock together.</span>]}
            />
          </h1>
          <p className="mt-6 max-w-xl text-base leading-relaxed text-white/70 sm:text-lg">
            Hit a personal best. Get it on camera. Tag {withIG("@myfeatherbomb")} on IG or{" "}
            <a
              href={IG_DM}
              target="_blank"
              rel="noreferrer"
              className="font-semibold text-[#EE4026] underline decoration-[#EE4026]/40 underline-offset-2 hover:decoration-[#EE4026]"
            >
              DM&nbsp;us
            </a>
            {" "}and your score could land on the Flock Board. New leaders crowned every month plus surprise
            prizes just for playing.
          </p>
          <a
            data-testid="host-event-button"
            href={IG_DM}
            target="_blank"
            rel="noreferrer"
            className="btn-primary mt-8 inline-flex items-center gap-2 rounded-full px-7 py-4 text-sm font-bold uppercase tracking-wide"
          >
            <PartyPopper size={18} /> Join the Flockboard
          </a>
        </div>
      </section>

      <BrandMarquee dark />

      {/* Photo wall */}
      <section className="bg-[#184786] px-5 py-24 sm:py-28">
        <div className="mx-auto max-w-[1400px]">
          <Reveal>
            <h2 className="font-display text-3xl uppercase leading-[1] text-white sm:text-5xl">
              Scenes from the <span className="text-[#EE4026]">flock</span>
            </h2>
          </Reveal>
          <div className="mt-10 grid grid-cols-2 gap-4 md:grid-cols-4">
            {[
              "https://customer-assets-7cd3h4nn.emergentagent.net/job_bomb-store-demo/artifacts/0fk2kt2c_YTDown.com_YouTube_Jianzi-The-traditional-Chinese-shuttleco_Media_9C4TmNQpPC4_001_1080p.mp4",
              "https://customer-assets-7cd3h4nn.emergentagent.net/job_bomb-store-demo/artifacts/iviaam3f_downloadgram.org_AQNQKPxzKsw7z5-5AXpojtLtFAqwAv1kRErH9drY8LuFKlEA_5XPSrXs1KrlsmNzlRu8yVZ8uFwGWgaPE1r9vUVpKNrQUWVkc-nhYGI.mp4",
              "https://customer-assets-7cd3h4nn.emergentagent.net/job_bomb-store-demo/artifacts/5qstob8f_tourismechinois%20-%20When%20you%20travel%20to%20Beijing%20the%20best%20moments%20arent%20in%20the%20guidebooks.%20Walk%20into%20a.mp4",
              "https://customer-assets-7cd3h4nn.emergentagent.net/job_bomb-store-demo/artifacts/61swjzkc_IMG_1397.webp",
              "https://customer-assets-7cd3h4nn.emergentagent.net/job_bomb-store-demo/artifacts/l6lwc9c5_date%20night.mp4",
              "https://customer-assets-7cd3h4nn.emergentagent.net/job_bomb-store-demo/artifacts/urarvkjg_IMG_9922.MOV",
              "https://customer-assets-7cd3h4nn.emergentagent.net/job_bomb-store-demo/artifacts/2irntfte_IMG_9908%20%281%29.MOV",
              "https://customer-assets-7cd3h4nn.emergentagent.net/job_bomb-store-demo/artifacts/38mrzm5s_IMG_0104.MOV",
            ].map((src, i) => (
              <Reveal key={i} delay={i * 0.08}>
                <div className="overflow-hidden rounded-2xl">
                  {/\.(mp4|mov|webm)$/i.test(src) ? (
                    <VideoTile src={src} />
                  ) : (
                    <img src={src} alt="" className="aspect-[3/4] w-full object-cover" />
                  )}
                </div>
              </Reveal>
            ))}
          </div>
        </div>
      </section>

      {/* Flock Board */}
      <section className="bg-[#F5F5F5] px-5 py-20 sm:px-8 sm:py-28">
        <div className="mx-auto flex max-w-[1400px] flex-col">
          <Reveal>
            <p className="font-mono text-xs uppercase tracking-[0.3em] text-[#184786]">How long can you keep it up?</p>
            <h2 className="font-display mt-3 text-4xl uppercase leading-[0.95] text-[#EE4026] sm:text-6xl">
              The Flock Board
            </h2>
          </Reveal>

          {/* Record cards */}
          <div className="mt-12 grid gap-6 md:grid-cols-3" data-testid="flockboard-records">
            {RECORDS.map((r, i) => (
              <Reveal key={r.cat} delay={i * 0.1}>
                <div className="flex h-full flex-col rounded-3xl border border-black/10 bg-white p-7">
                  <span className="font-mono text-xs uppercase tracking-[0.25em] text-[#EE4026]">{r.cat}</span>
                  <h3 className="font-display mt-2 text-2xl uppercase text-[#212121]">{r.title}</h3>
                  <p className="mt-2 flex-1 text-sm leading-relaxed text-[#212121]/60">{r.desc}</p>
                  <div className="mt-5 rounded-2xl bg-[#F5F5F5] p-4">
                    <p className="font-mono text-[10px] uppercase tracking-widest text-[#212121]/50">Current record</p>
                    <p className="font-display mt-1 text-3xl text-[#212121]">
                      🏆 <span className="text-[#EE4026]">{r.record.split(" ")[0]}</span>&nbsp;{r.record.split(" ").slice(1).join(" ")}
                    </p>
                    <p className="mt-1 text-xs text-[#184786]">{r.who}</p>
                  </div>
                  <a
                    href={IG_DM}
                    target="_blank"
                    rel="noreferrer"
                    className="btn-primary mt-5 inline-flex items-center justify-center gap-2 rounded-full py-3 text-xs font-bold uppercase tracking-wide"
                  >
                    {r.cta} →
                  </a>
                </div>
              </Reveal>
            ))}
          </div>

          <p className="mt-8 font-mono text-sm uppercase tracking-[0.2em] text-[#184786]">
            View all submissions{" "}
            <a
              href={IG_URL}
              target="_blank"
              rel="noreferrer"
              className="font-semibold text-[#EE4026] underline decoration-[#EE4026]/40 underline-offset-2 hover:decoration-[#EE4026]"
            >
              here
            </a>
          </p>

          {/* September's Flockboard Awards */}
          <div className="order-last mt-24 overflow-hidden rounded-[2rem] bg-[#184786] p-8 sm:p-12" data-testid="flockboard-awards">
            <p className="font-mono text-xs uppercase tracking-[0.3em] text-white/60">This month's rewards</p>
            <h3 className="font-display mt-3 text-3xl uppercase leading-[0.95] text-white sm:text-5xl">
              September's <span className="text-[#EE4026]">Flockboard</span> Awards
            </h3>
            <div className="mt-8 grid gap-5 md:grid-cols-2">
              {[
                { icon: Gift, title: "$50 Craft Beer Market Gift Card", desc: "Cheers to this month's top scorer.", image: "https://customer-assets-7cd3h4nn.emergentagent.net/job_bomb-store-demo/artifacts/e1ufu9lj_Beer_Marg_GiftCard_Landscape-scaled.webp", fit: "cover" },
                { icon: Sparkles, title: "Collagen-Infused Body Cream", desc: "Perfect for your skin this fall.", image: "https://customer-assets-7cd3h4nn.emergentagent.net/job_bomb-store-demo/artifacts/cvkg8wek_WhatsApp%20Image%202026-08-28%20at%207.35.55%20PM.jpeg", fit: "cover" },
              ].map((p) => (
                <div key={p.title} className="flex flex-col overflow-hidden rounded-2xl bg-white">
                  <div className="h-44 w-full bg-[#F5F5F5]">
                    <img
                      src={p.image}
                      alt={p.title}
                      className={`h-full w-full ${p.fit === "contain" ? "object-contain p-3" : "object-cover"}`}
                    />
                  </div>
                  <div className="flex items-start gap-4 p-6">
                    <div className="grid h-12 w-12 shrink-0 place-items-center rounded-full bg-[#EE4026]/10 text-[#EE4026]">
                      <p.icon size={22} />
                    </div>
                    <div>
                      <p className="font-display text-xl uppercase leading-tight text-[#212121]">{p.title}</p>
                      <p className="mt-1 text-sm text-[#212121]/60">{p.desc}</p>
                    </div>
                  </div>
                </div>
              ))}
            </div>
            <p className="mt-8 font-mono text-xs uppercase tracking-[0.2em] text-white/60">
              Winners will be announced at month end once all submissions are tallied
            </p>
          </div>

          {/* How to get on the board */}
          <div className="mt-24">
            <Reveal>
              <h3 className="font-display text-3xl uppercase leading-[0.95] text-[#212121] sm:text-5xl">
                How to get on the board
              </h3>
            </Reveal>
            <div className="mt-10 grid gap-6 sm:grid-cols-2 lg:grid-cols-4">
              {STEPS.map(([n, t, d], i) => (
                <Reveal key={n} delay={i * 0.08}>
                  <div className="h-full rounded-2xl border border-black/10 bg-white p-6">
                    <span className="font-display text-4xl text-[#EE4026]/80">{n}</span>
                    <p className="mt-3 font-bold uppercase tracking-wide text-[#212121]">{t}</p>
                    <p className="mt-2 text-sm leading-relaxed text-[#212121]/60">
                      {i === 2 ? (
                        <>
                          Post it on IG and tag {withIG("@myfeatherbomb")} or DM your video to us{" "}
                          <a
                            href={IG_DM}
                            target="_blank"
                            rel="noreferrer"
                            className="font-semibold text-[#EE4026] underline decoration-[#EE4026]/40 underline-offset-2 hover:decoration-[#EE4026]"
                          >
                            here
                          </a>
                          .
                        </>
                      ) : (
                        withIG(d)
                      )}
                    </p>
                  </div>
                </Reveal>
              ))}
            </div>
            <p className="mt-10 font-mono text-sm uppercase tracking-[0.2em] text-[#184786]">
              Monthly champs win prizes. Random players do too.
            </p>
          </div>
        </div>
      </section>

      <HostModal open={modal} onClose={() => setModal(false)} />
    </div>
  );
};

export default Community;
